import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useModalState } from 'packages/core'
import { EidButton, TextInput, WarningModal } from 'packages/eid-ui'
import { Box, Typography, useTheme } from '@mui/material'
import { Icon } from 'packages/eid-icons'
import ActionModal from './ActionModal'
import {
    useCreateCustomCategory,
    useUpdateCustomCategory,
    useDeleteCategory,
} from 'hooks'
import { useHistory } from 'react-router'

const ManageCategoryButton = ({ label = '', category = null }) => {
    const theme = useTheme()
    const history = useHistory()

    const { t } = useTranslation()
    const [showWarningModal, openWarningModal, closeWarningModal] =
        useModalState()
    const [showAddEditPopup, openAddEditPopup, closeAddEditModal] =
        useModalState()

    const handleAddKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openAddEditPopup()
        }
    }

    const [categoryname, setCategoryName] = useState(category?.name)
    const isEditMode = category != null

    const { mutateAsync: createCategory, isLoading: isCreating } =
        useCreateCustomCategory()
    const { mutateAsync: updateCategory, isLoading: isUpdating } =
        useUpdateCustomCategory()

    const { mutateAsync: deleteCategory, isLoading: isDeleting } =
        useDeleteCategory()

    const handleSubmit = () => {
        if (!categoryname || categoryname.trim() === '') {
            return
        }
        if (isEditMode) {
            try {
                updateCategory({
                    categoryId: category?.id,
                    name: categoryname,
                    friendlyName: categoryname,
                }).then(() => {
                    closeAddEditModal()
                    setCategoryName('')
                })
            } catch (err) {}
        } else {
            try {
                createCategory({
                    name: categoryname,
                    friendlyName: categoryname,
                }).then(() => {
                    closeAddEditModal()
                    setCategoryName('')
                })
            } catch (err) {}
        }
    }

    const handleDeleteCategory = () => {
        deleteCategory([category?.id]).then(() => {
            closeWarningModal()
            const {
                location: { pathname },
            } = history
            const pathSegments = pathname.split('/')
            if (pathSegments.length > 1) {
                pathSegments.pop()
            }
            const newPath = pathSegments.join('/')

            history.push(`${newPath}/all`)
        })
    }

    return (
        <>
            {showAddEditPopup && (
                <ActionModal
                    headerTitle={
                        isEditMode
                            ? t('EditSSOCategoryPopupTitle')
                            : t('AddSSOCategoryPopupTitle')
                    }
                    onSubmit={handleSubmit}
                    onClose={() => {
                        closeAddEditModal()
                        setCategoryName(category?.name)
                    }}
                    disabled={Boolean(
                        isCreating ||
                            isUpdating ||
                            !categoryname ||
                            categoryname.trim() === '',
                    )}
                    isSubmitting={isCreating || isUpdating}
                    actionButtonLabel={t('Save')}
                >
                    <Box sx={{ width: '61.1rem', marginBottom: '1.6rem' }}>
                        <p>
                            {isEditMode
                                ? t('EditSSOCategoryPopupDescription')
                                : t('AddSSOCategoryPopupDescription')}
                        </p>
                        <TextInput
                            placeholder={t('CategoryName')}
                            label={
                                <>
                                    {t('Category')} <span>*</span>
                                </>
                            }
                            required={true}
                            value={categoryname}
                            validationError={t('Required')}
                            handleChange={(val) => {
                                setCategoryName(val)
                            }}
                        />
                    </Box>
                </ActionModal>
            )}

            {showWarningModal && (
                <WarningModal
                    proceeding={isDeleting}
                    open={openWarningModal}
                    title={t('Confirm')}
                    description={t('ConfirmDeleteCategory', {
                        name: category?.name,
                    })}
                    proceed={() => {
                        handleDeleteCategory()
                    }}
                    cancel={closeWarningModal}
                    yesLabel={t('Yes')}
                    noLabel={t('No')}
                />
            )}
            {!isEditMode && (
                <Box
                    display="flex"
                    marginLeft="2.5rem"
                    tabIndex={0}
                    role="button"
                    aria-label="Add"
                    onKeyDown={handleAddKeyDown}
                    onClick={openAddEditPopup}
                    data-ai-explain="Add a new SSO application category"
                    sx={{
                        cursor: 'pointer',
                    }}
                >
                    <Icon name="Plus" color={theme?.palette?.primary?.main} />
                    <Typography
                        sx={{
                            textTransform: 'none',
                            minWidth: 'fit-content',
                            fontWeight: theme.typography.fontWeightRegular,
                            fontSize: '1.3rem',
                            marginLeft: '0.8rem',
                            marginRight: '0.8rem',
                            color: theme?.palette?.primary?.main,
                        }}
                    >
                        {t('Add')}
                    </Typography>
                </Box>
            )}
            {isEditMode && (
                <EidButton.DropdownOptionsButton
                    label={label}
                    data-ai-explain="Manage this category: rename or delete"
                    options={[
                        {
                            name: 'edit',
                            onClick: openAddEditPopup,
                            label: t('Edit'),
                        },
                        {
                            name: 'remove',
                            onClick: openWarningModal,
                            label: t('Remove'),
                        },
                    ]}
                    onClick={() => console.log('Button Clicked')}
                    parentStyle={{
                        height: '4rem',
                        width: '18rem',
                        '& .MuiButton-icon': {
                            borderLeft: '1px solid rgba(255, 255, 255, 0.25)',
                            padding: '.5rem',
                        },
                    }}
                    rootStyles={
                        {
                          width: '18.4rem',
                          marginTop: '0.4rem',
                          padding: '0 0.6rem',
                        }
                    }
                />
            )}
        </>
    )
}

export default ManageCategoryButton
